import React from "react";

const ZONE_COLOUR = { red: "#ef4444", amber: "#f59e0b", green: "#10b981" };

function fmtValue(v, unit) {
  if (v === null || v === undefined) return "—";
  if (unit === "currency") {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (Math.abs(v) >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`;
    return `$${v.toFixed(0)}`;
  }
  if (unit === "pct")   return `${(v * 100).toFixed(1)}%`;
  if (unit === "ratio") return `${(v * 100).toFixed(1)}%`;
  if (typeof v === "number") return v.toFixed(2);
  return String(v);
}

/**
 * Power BI-style gauge dial with 3 coloured zones.
 * `zones` is an array of {to, colour} in ascending order. `min`/`max` bound the dial.
 * `value` is drawn as a needle. Rendered value below with proper unit formatting.
 */
export function BIGauge({ label, value, unit = "number", min = 0, max = 100, zones = [], size = 150 }) {
  const cx = size / 2;
  const cy = size * 0.62;
  const r = size * 0.40;
  const stroke = size * 0.12;

  const clamped = Math.max(min, Math.min(max, value ?? min));
  const pctPos = (v) => (Math.max(min, Math.min(max, v)) - min) / (max - min || 1);
  const angleOf = (v) => Math.PI - pctPos(v) * Math.PI;
  const point = (v) => {
    const a = angleOf(v);
    return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) };
  };
  const arc = (from, to) => `M ${from.x} ${from.y} A ${r} ${r} 0 0 1 ${to.x} ${to.y}`;

  // Build zone segments in ascending "to" order
  const sorted = [...zones].sort((a, b) => a.to - b.to);
  const segments = [];
  let prev = min;
  for (const z of sorted) {
    const to = Math.min(max, z.to);
    if (to > prev) {
      segments.push({ from: prev, to, colour: z.colour });
      prev = to;
    }
  }
  if (prev < max) segments.push({ from: prev, to: max, colour: "#d1d5db" });

  const needle = point(clamped);
  const nxLen = r - stroke / 2 - 2;
  const nAngle = angleOf(clamped);
  const nx = cx + nxLen * Math.cos(nAngle);
  const ny = cy - nxLen * Math.sin(nAngle);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.72} viewBox={`0 0 ${size} ${size * 0.72}`}>
        {segments.map((s, i) => (
          <path key={i}
                d={arc(point(s.from), point(s.to))}
                fill="none"
                stroke={ZONE_COLOUR[s.colour] || s.colour}
                strokeWidth={stroke}
                strokeLinecap="butt" />
        ))}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#0f172a" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={3.5} fill="#0f172a" />
      </svg>
      <div className="text-center -mt-2">
        <div className="text-[11px] text-slate-600 leading-tight max-w-[150px]">{label}</div>
        <div className="font-display text-xl font-semibold text-slate-900">{fmtValue(value, unit)}</div>
      </div>
    </div>
  );
}
